import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, take } from 'rxjs/operators';

import { Account, AccountPlanEligibility, AllowedLines, ProfileLines, User } from './models';
import { StoreAction } from 'store/actions';
import { ApiChannel } from 'core/constants';
import { buildHeaders, XmStore } from 'core/services';
import { ModelBase } from '../model-base';

@Injectable()
export class UserApi {
    public static getAccountInfo(_xmStore: XmStore, http: HttpClient): Observable<Account> {
        return http.get('/xm/ordertech/account', {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY }),
            params: { includeInsurance: 'true' }
        }).pipe(
            map((response: Account) => Account.create<Account>(response))
        );
    }

    public static getAllowedLines(_xmStore: XmStore, http: HttpClient): Observable<AllowedLines> {
        return http.get('/account/allowedlines', {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY }),
            params: { checkCredibility: 'false' }
        }).pipe(
            map((response: ApiResponse) => AllowedLines.create<AllowedLines>(response)),
            catchError(() => of(AllowedLines.create<AllowedLines>({ maxLines: 0, allowedLines: 0 })))
        );
    }

    public static updateAccountInfo(xmStore: XmStore, http: HttpClient, notificationContact: NotificationContact): Observable<User> {
        return http.post('/account/settings/notification', notificationContact, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            mergeMap(() => xmStore.peek<User>(User).pipe(
                map((storedUser: User) => {

                    storedUser.account.notificationContacts = notificationContact;

                    return storedUser;
                }),
                take(1)
            ))
        );
    }

    public static getAccountPlanEligibility(_xmStore: XmStore, http: HttpClient): Observable<AccountPlanEligibility> {
        return http.get('/account/plans/eligibility', {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: AccountPlanEligibility) => AccountPlanEligibility.create<AccountPlanEligibility>(response)));
    }

    public static getProfileLines(_xmStore: XmStore, http: HttpClient): Observable<ProfileLines> {
        return http.get('/identity-v2/invite/profile/lines', {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((response: ProfileLines) => ProfileLines.create<ProfileLines>(response)));
    }
}

ModelBase.fetchMapping[StoreAction.GET_ACCOUNT_INFO] = UserApi.getAccountInfo;
ModelBase.persistMapping[StoreAction.UPDATE_ACCOUNT_INFO] = UserApi.updateAccountInfo;
ModelBase.fetchMapping[StoreAction.GET_ALLOWED_LINES] = UserApi.getAllowedLines;
ModelBase.fetchMapping[StoreAction.GET_ACCOUNT_PLAN_ELIGIBILITY] = UserApi.getAccountPlanEligibility;
ModelBase.fetchMapping[StoreAction.GET_PROFILE_LINES] = UserApi.getProfileLines;
